import { Token } from "../store/global/TokenEnum";
import { arbitrum, Chain, mainnet, polygon } from "viem/chains";
import React from "react";
import { Amount } from "../types/amount";
import {
  BebopperIcon,
  OgAchievoorIcon,
  OgCommunityCallFrenIcon,
  OgContributoorIcon,
  OgOverachievoorIcon,
  OgTestoorIcon,
  OgTradoorIcon,
  PinHoldoorIcon,
  QuestoorIcon,
  TwitterMaxiIcon,
} from "components/Icons";

export const enum BEBOP_ROUTES {
  Home = "/",
  Trade = "/trade",
  Activity = "/activity",
  Wrap = "/wrap",
  PageNotFound = "*",
}
export const MAX_SELECTABLE_TOKENS = 5;
export const NATIVE_TOKENS = ["ETH", "BTC", "MATIC"];
export const QUOTE_REFETCH_INTERVAL_MS = 10_000;
export const BALANCE_REFETCH_INTERVAL_MS = 30_000;
export const TOKEN_REFETCH_INTERVAL_MS = 30_000;
export const FETCH_BALANCE_RETRY_TIMES = 3;
export const FETCH_TOKENS_RETRY_TRIES = 3;
export const USER_INPUT_DEBOUNCE_TIME_MS = 250;
export const DIGITS_TO_SHOW_ON_BALANCE = 5;
export const DIGITS_TO_SHOW_ON_CONFIRM = 5;
export const QUOTE_DEBOUNCE_TIME = 1000;

export const SEPARATOR_DOT = "•";
export const BEBOP_GUILD_ID = 11177;
export const GUILD_NAMES_TO_ID = {
  OG_CONTRIBUTOOR: 23153,
  CONTRIBUTOOR: 43738,
  OG_OVERACHIEVOOR: 21322,
  OG_TRADOOR: 21320,
  OG_ACHIEVOOR: 21321,
  OG_TESTOOR: 21319,
  OG_COMMUNITY_CALL_FREN: 17064,
  PIN_HOLDOOR: 56300,
  QUESTOOR: 28281,
  TWITTER_MAXI: 17063,
  BEBOPPER: 56299,
  TRADOOR: 61741,
  DEV_MOCK: 99999,
};
export const GUILD_ROLES_PRECEDENCE: Record<number, number> = [
  GUILD_NAMES_TO_ID.OG_CONTRIBUTOOR,
  GUILD_NAMES_TO_ID.OG_ACHIEVOOR,
  GUILD_NAMES_TO_ID.OG_TRADOOR,
  GUILD_NAMES_TO_ID.OG_ACHIEVOOR,
  GUILD_NAMES_TO_ID.OG_TESTOOR,
  GUILD_NAMES_TO_ID.TRADOOR,
  GUILD_NAMES_TO_ID.PIN_HOLDOOR,
  GUILD_NAMES_TO_ID.QUESTOOR,
  GUILD_NAMES_TO_ID.OG_COMMUNITY_CALL_FREN,
  GUILD_NAMES_TO_ID.TWITTER_MAXI,
  GUILD_NAMES_TO_ID.BEBOPPER,
].reduce((prev, curr, idx) => {
  return {
    ...prev,
    [curr]: idx,
  };
}, {});

export const GUILD_ROLES_EMOJIS: Record<number, React.FC<React.SVGProps<SVGSVGElement>>> = {
  [GUILD_NAMES_TO_ID.OG_CONTRIBUTOOR]: OgContributoorIcon,
  [GUILD_NAMES_TO_ID.OG_OVERACHIEVOOR]: OgOverachievoorIcon,
  [GUILD_NAMES_TO_ID.OG_TRADOOR]: OgTradoorIcon,
  [GUILD_NAMES_TO_ID.OG_ACHIEVOOR]: OgAchievoorIcon,
  [GUILD_NAMES_TO_ID.OG_TESTOOR]: OgTestoorIcon,
  [GUILD_NAMES_TO_ID.QUESTOOR]: QuestoorIcon,
  [GUILD_NAMES_TO_ID.PIN_HOLDOOR]: PinHoldoorIcon,
  [GUILD_NAMES_TO_ID.TWITTER_MAXI]: TwitterMaxiIcon,
  [GUILD_NAMES_TO_ID.BEBOPPER]: BebopperIcon,
  [GUILD_NAMES_TO_ID.TRADOOR]: OgTradoorIcon,
  [GUILD_NAMES_TO_ID.OG_COMMUNITY_CALL_FREN]: OgCommunityCallFrenIcon,
  [GUILD_NAMES_TO_ID.DEV_MOCK]: BebopperIcon,
};

export const TOKEN_DECIMALS_MAP: Partial<Record<Token, number>> = {
  [Token["1INCH"]]: 18,
  [Token.AAVE]: 18,
  [Token.AMP]: 18,
  [Token.APE]: 18,
  [Token.BAL]: 18,
  [Token.BAND]: 18,
  [Token.BAT]: 18,
  [Token.BLUR]: 18,
  [Token.BUSD]: 18,
  [Token.CBETH]: 18,
  [Token.CEL]: 4,
  [Token.COMP]: 18,
  [Token.CRO]: 8,
  [Token.CRV]: 18,
  [Token.CTX]: 18,
  [Token.DAI]: 18,
  [Token.DPI]: 18,
  [Token.DYDX]: 18,
  [Token.ENS]: 18,
  [Token.ETH]: 18,
  [Token.FET]: 18,
  [Token.FTM]: 18,
  [Token.FTT]: 18,
  [Token.GALA]: 8,
  [Token.GALA_OLD]: 8,
  [Token.GALA_V1]: 8,
  [Token.GMX]: 18,
  [Token.GRT]: 18,
  [Token.HFT]: 18,
  [Token.IMX]: 18,
  [Token.LDO]: 18,
  [Token.LINK]: 18,
  [Token.MAGIC]: 18,
  [Token.MPL]: 18,
  [Token.MATIC]: 18,
  [Token.MKR]: 18,
  [Token.OMG]: 18,
  [Token.PAX]: 18,
  [Token.PERP]: 18,
  [Token.PEPE]: 18,
  [Token.RDNT]: 18,
  [Token.REN]: 18,
  [Token.RENBTC]: 8,
  [Token.SAND]: 18,
  [Token.SHIB]: 18,
  [Token.SUSHI]: 18,
  [Token.TUSD]: 18,
  [Token.UNI]: 18,
  [Token.USDC]: 6,
  [Token.USDP]: 18,
  [Token.USDT]: 6,
  [Token.USTC]: 18,
  [Token.WBTC]: 8,
  [Token.WETH]: 18,
  [Token.WMATIC]: 18,
  [Token.YFI]: 18,
  [Token.YGG]: 18,
  [Token.ZRX]: 18,
};

export const DUMMY_WALLET = "0x0000000000000000000000000000000000000001";
export const BLOCK_EXPLORER_URL_BY_NETWORK: Record<number, string> = {
  [mainnet.id]: "https://etherscan.io/",
  [polygon.id]: "https://polygonscan.com/",
  [arbitrum.id]: "https://arbiscan.io/",
};
export const NETWORK_NAME_TO_DISPLAY: Record<number, string> = {
  [mainnet.id]: mainnet.name,
  [polygon.id]: polygon.name,
  [arbitrum.id]: "Arbitrum",
};

export const GAS_ESTIMATE_MULTIPLIER = BigInt(2);

export const TWITTER_IMAGE_URL = "https://twitter.com/";
export const TWITTER_IMAGE_URLS = [
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206635948675075/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206432894222336/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206386643443712/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206339839365120/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206251897421824/photo/1`,
  `${TWITTER_IMAGE_URL}bbop90210/status/1596206201741930496/photo/1`,
];

export const TIME_BEFORE_USER_IS_IDLE = 60_000;

export const FALLBACK_DISPLAY_DECIMALS = 5;

export const NAV_LINKS = {
  DISCORD: "https://discord.com/invite/bebop",
  TWITTER: "https://twitter.com/bebop_dex",
  MEDIUM: "https://medium.com/bebop-dex",
  BLOG: "https://medium.com/bebop-dex",
  FAQ: "https://help.bebop.xyz/en",
  JOBS: "https://jobs.lever.co/Bebop",
  EMAIL: "mailto:hello@bebop.xyz",
  BRAND: "https://docs.bebop.xyz/bebop/brand-guidance/logos",
  TERMS_AND_CONDITIONS: "/terms.pdf",
  PRIVACY_POLICY: "/privacy.pdf",
  DOCUMENTATION: "https://docs.bebop.xyz",
  DOCUMENTATION_API_INTRODUCTION: "https://docs.bebop.xyz/bebop/bebop-api/api-introduction",
  NOOX: "https://noox.world/collection/bebop-collection",
  GUILD: "https://guild.xyz/bebop",
};

export const SOCIAL_CTA_LINKS = {
  SHARE_TWEET_PREFIX: "https://twitter.com/intent/tweet?text=",
};

export const ACCORDIONS = {
  SWAPANDSEND: "swap-and-send",
  DETAILS: "trade-details",
} as const;

export const PERMIT_TWO_CONTRACT_ADDRESS = "0x000000000022d473030f116ddee9f6b43ac78ba3";
export const MAX_UINT_256 = BigInt(
  "115792089237316195423570985008687907853269984665640564039457584007913129639935"
);
export const MAX_UINT_160 = BigInt("1461501637330902918203684832716283019655932542975");

export const USDT_CONTRACTS = {
  [mainnet.id]: "0xdAC17F958D2ee523a2206206994597C13D831ec7",
};

export const OMG_CONTRACTS = {
  [mainnet.id]: "0xd26114cd6EE289AccF82350c8d8487fedB8A0C07",
};

export const ALCHEMY_API_KEY = "GjpKGlKJy4WtofaSCs_5_B7NZSX8K0vw";
export const ZERION_NFT_CONTRACT = "0x932261f9fc8da46c4a22e31b45c4de60623848bf";

export const DEFAULT_DECIMAL_SEPARATOR = ".";

export const SENTRY_CONTEXTS = {
  APPROVALS: "approvals",
  BALANCES: "balances",
  ALLOWANCES: "allowances",
};

export const enum Filter {
  Network = "network",
  Transaction = "transaction",
  Token = "token",
  Amount = "amount",
  Date = "date",
}
export const enum TransactionType {
  Trade = "Trade",
  TradeAndSend = "Trade And Send",
}

export interface TransactionTypeProps {
  icon: React.ReactNode;
}

export interface IAmountFilter {
  min: Amount | undefined;
  max: Amount | undefined;
}

export interface FiltersInterface {
  [Filter.Network]: Array<Chain["name"]> | undefined;
  [Filter.Transaction]: Array<TransactionType> | undefined;
  [Filter.Token]: Array<Token> | undefined;
  [Filter.Amount]: IAmountFilter | undefined;
  [Filter.Date]: Array<string> | undefined;
}

export const FilterOptions = {
  Network: [mainnet.name, polygon.name, arbitrum.name],
  Transaction: ["Trade", "Approval", "Revoke", "Wrapping", "Unwrapping"],
  Token: Object.keys(Token) as Array<Token>,
  Date: ["All", "1 Day", "1 Week", "1 Month", "3 Months", "6 Months", "12 Months"],
};

export const NUM_TRADES_TO_FETCH = 500;
export const DAI_LIKE_PERMIT1_TOKENS = [Token.RENBTC, Token.DAI];
